import React, {FC} from 'react';
import styled from 'styled-components';

import {AnalyticsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {LiveDashboardContentModel} from '../../../models/api/liveDashboardContentModel';
import {LiveDashboardAssignedByTeammateSection} from './assignedByTeammate/liveDashboardAssignedByTeammateSection';
import {LIVE_DASHBOARD_MAX_WIDTH, LIVE_DASHBOARD_REDUCED_GAP_BREAKPOINT_VIEWPORT_WIDTH} from './constants';
import {LiveDashboardHeadlineMetrics} from './liveDashboardHeadlineMetrics';
import {LiveDashboardOldestOpenConversations} from './oldestOpenConversations/liveDashboardOldestOpenConversations';
import {LiveDashboardWorkloadByInboxSection} from './workloadByInbox/liveDashboardWorkloadByInboxSection';

interface LiveDashboardContentProps {
  panelId: AnalyticsPanelId;
  selectedInboxIds: ReadonlyArray<number>;
  hasWaitingMetrics: boolean;
  liveDashboardContent: LiveDashboardContentModel | undefined;
  isThisPoppedOut: boolean;
  isBetaBannerOpen: boolean;
}

const StyledOuterGridDiv = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(min-content, 1fr);
  max-width: ${LIVE_DASHBOARD_MAX_WIDTH}px;
  margin: auto;

  gap: 24px;

  @media screen and (max-width: ${LIVE_DASHBOARD_REDUCED_GAP_BREAKPOINT_VIEWPORT_WIDTH}px) {
    gap: 12px;
  }
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 24px;
  align-content: start;

  @media screen and (max-width: ${LIVE_DASHBOARD_REDUCED_GAP_BREAKPOINT_VIEWPORT_WIDTH}px) {
    gap: 12px;
  }
`;

const StyledSpanAllColumnsDiv = styled.div`
  grid-column: 1 / span 12;
`;

const StyledLiveDashboardAssignedByTeammateSection = styled(LiveDashboardAssignedByTeammateSection)<{
  $headerAndTopbarHeight: number;
}>`
  top: 0;
  position: sticky;
  align-self: start;

  /*
    This is pretty ugly, but I don't think there's another way of doing this given the following constraints:
      - the scrollbar must be at the far right of the screen
      - this section shouldn't scroll with the rest of the grid

    Remove any of those constraints and we should be able to avoid this ugliness.
  */
  height: calc(100vh - ${(p) => p.$headerAndTopbarHeight}px - 48px);

  @media screen and (max-width: ${LIVE_DASHBOARD_REDUCED_GAP_BREAKPOINT_VIEWPORT_WIDTH}px) {
    height: calc(100vh - ${(p) => p.$headerAndTopbarHeight}px - 24px);
  }

  overflow-y: hidden;
`;

export const LiveDashboardContent: FC<LiveDashboardContentProps> = ({
  panelId,
  liveDashboardContent,
  selectedInboxIds,
  hasWaitingMetrics,
  isThisPoppedOut,
  isBetaBannerOpen,
}) => {
  const {oldestOpenConversationIds, assignedByTeammate, workloadByInbox} = liveDashboardContent ?? {};

  const reportHeaderHeight = 144; // px

  // There is no P0 in a popup.
  const p0Height = isThisPoppedOut ? 0 : 52; // px
  const betaBannerHeight = isBetaBannerOpen ? 47 : 0;
  const headerAndTopbarHeight = reportHeaderHeight + p0Height + betaBannerHeight;

  return (
    <StyledOuterGridDiv>
      <StyledGrid>
        <LiveDashboardHeadlineMetrics
          selectedInboxIds={selectedInboxIds}
          liveDashboardContent={liveDashboardContent}
          hasWaitingMetrics={hasWaitingMetrics}
          isThisPoppedOut={isThisPoppedOut}
        />
        <StyledSpanAllColumnsDiv>
          <LiveDashboardWorkloadByInboxSection
            workloadByInbox={workloadByInbox}
            hasWaitingMetrics={hasWaitingMetrics}
          />
        </StyledSpanAllColumnsDiv>
        <StyledSpanAllColumnsDiv>
          <LiveDashboardOldestOpenConversations
            panelId={panelId}
            oldestOpenConversationIds={oldestOpenConversationIds}
            isThisPoppedOut={isThisPoppedOut}
            hasWaitingMetrics={hasWaitingMetrics}
          />
        </StyledSpanAllColumnsDiv>
      </StyledGrid>
      <StyledLiveDashboardAssignedByTeammateSection
        panelId={panelId}
        assignedByTeammate={assignedByTeammate}
        $headerAndTopbarHeight={headerAndTopbarHeight}
      />
    </StyledOuterGridDiv>
  );
};
